/* eslint-disable react/jsx-props-no-spreading */
import { Box, Button, styled } from '@mui/material';
import { FormProvider, SubmitHandler, useForm, Controller } from 'react-hook-form';
import { Editor } from '@tinymce/tinymce-react';
import resolver from './CreateOrEditQuestionForm.validation';
import FormInput from 'UI/Form/FormInput';
import {
  useCreateFAQMutation,
  useUpdateFAQMutation,
} from '@Store/api/policiesSlice';
import { ICreateOrEditFAQ, IFAQ } from '@Types/FAQ';

// ✅ Reads API key from your .env file (same as EditContentModal)
const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

const FormContainer = styled(Box)`
  display: flex;
  flex-direction: column;
  height: 100%;

  & .MuiFormHelperText-root {
    color: red !important;
  }
`;

interface IProps {
  FAQ?: IFAQ;
  readOnly?: boolean;
  handleClose: () => void;
}

export function CreateOrEditQuestionForm({
  FAQ,
  readOnly,
  handleClose,
}: IProps) {
  const [createFAQ] = useCreateFAQMutation();
  const [updateFAQ] = useUpdateFAQMutation();

  const methods = useForm<ICreateOrEditFAQ>({
    defaultValues: {
      question: FAQ?.question ?? '',
      answer: FAQ?.answer ?? '',
      order: FAQ?.order ?? 0,
    },
    resolver,
  });
  const { control, handleSubmit } = methods;

  const handleFormSubmit: SubmitHandler<ICreateOrEditFAQ> = async (
    data
  ): Promise<void> => {
    if (FAQ) {
      const _response = await updateFAQ({
        id: FAQ.id,
        ...data,
      }).unwrap();
    } else {
      const _response = await createFAQ(data).unwrap();
    }
    handleClose();
  };

  return (
    <FormProvider {...methods}>
      <FormContainer
        component={'form'}
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        {/* 1. Question field - plain text */}
        <div style={{ marginTop: 0 }}>
          <FormInput
            autoHeight
            multiline
            label='Question'
            name='question'
            readOnly={readOnly}
            rows={4}
            type='text'
          />
        </div>

        {/* 2. Answer field - NOW with FULL rich-text editor (matches Terms page) */}
        <div style={{ marginTop: 30 }}>
          <Controller
            name="answer"
            control={control}
            render={({ field }) => (
              <Editor
                apiKey={TINYMCE_API_KEY}
                value={field.value}
                onEditorChange={(content) => field.onChange(content)}
                disabled={readOnly}
                init={{
                  height: 400, // Slightly smaller than Terms (400 vs 500) to fit the modal
                  menubar: true, // ✅ This shows the "File | Edit | View | Insert | Format..." menu!
                  branding: false,
                  statusbar: false,
                  plugins:
                    'advlist anchor autolink codesample fullscreen help image ' +
                    'lists link media preview searchreplace table template visualblocks wordcount ' +
                    'paste',
                  toolbar:
                    'undo redo | formatselect fontfamily fontsize | ' + // ✅ Font family/size dropdowns
                    'bold italic underline superscript subscript | backcolor | ' +
                    'alignleft aligncenter alignright alignjustify | ' +
                    'bullist numlist outdent indent | removeformat | help code',
                  menu: {
                    format: {
                      title: 'Format',
                      items: 'bold italic underline | formats | fontsize fontfamily | removeformat',
                    },
                  },
                  block_formats: 'Paragraph=p; Small Text=h6; Medium Text=h5; Large Text=h4; Extra Large=h3; Huge=h2; Giant=h1',
                  entity_encoding: 'raw',
                  valid_elements: '*[*]',
                  extended_valid_elements:
                    'sup,sub,span[*],div[*],p[*],h1[*],h2[*],h3[*],h4[*],h5[*],h6[*],a[*]',
                  forced_root_block: 'p',
                  paste_as_text: false,
                  paste_data_images: false,
                  paste_webkit_styles: 'font-weight font-style color background-color',
                  paste_remove_styles_if_webkit: true,
                  paste_merge_formats: true,
                  content_style:
                    'body { font-family: Helvetica, Arial, sans-serif; font-size:14px; line-height:1.6; }' +
                    'h1 { font-size: 32px !important; font-weight: bold; }' +
                    'h2 { font-size: 28px !important; font-weight: bold; }' +
                    'h3 { font-size: 24px !important; font-weight: bold; }' +
                    'h4 { font-size: 20px !important; font-weight: bold; }' +
                    'h5 { font-size: 16px !important; font-weight: bold; }' +
                    'h6 { font-size: 12px !important; font-weight: bold; }' +
                    'p { font-size: 14px !important; margin-bottom: 1em; }',
                }}
              />
            )}
          />
        </div>

        {/* 3. Order field - plain text */}
        <div style={{ marginTop: 30 }}>
          <FormInput
            label='Order'
            name='order'
            readOnly={readOnly}
            type='text'
          />
        </div>

        {!readOnly && (
          <Button
            fullWidth
            sx={{ mt: '30px', textTransform: 'capitalize', fontSize: 16 }}
            type='submit'
            variant='contained'
          >
            {FAQ ? 'Save Changes' : 'Add New Question'}
          </Button>
        )}
      </FormContainer>
    </FormProvider>
  );
}